import type { Vec2, FocusAimState } from '../types.js';

export type ClientToWorld = (clientX: number, clientY: number) => Vec2;

function normalizeOrZero(x: number, y: number): Vec2 {
  const len = Math.hypot(x, y);
  if (len < 1e-8) return { x: 0, y: 0 };
  return { x: x / len, y: y / len };
}

/**
 * Mouse input adapter.
 *
 * - Tracks the cursor position relative to the canvas.
 * - Right mouse button (RMB) activates Focus Aim.
 * - LEFT mouse button: a quick click (tap) fires the manual turret toward the
 *   click point; holding (≥250 ms or an 8 px drag) makes the ship FOLLOW the
 *   cursor — mobile-style pointer steering on the desktop.
 * - Emits a world-space aim direction from the player to the cursor each tick.
 *
 * If no custom `clientToWorld` mapping is supplied, the adapter assumes the
 * camera is centered on the player and computes the aim direction from the
 * screen-center offset.
 */
export class Mouse {
  private clientX = 0;
  private clientY = 0;
  private rmb = false;
  private lmb = false;
  private firePressed = false;
  /** Direction from screen center (the ship) to the last LMB click. */
  private fireDir: Vec2 | null = null;
  private usedThisFrame = false;
  // LMB tap-vs-hold state machine: tap = turret fire, hold = ship follows.
  private lmbDownAt = 0;
  private lmbDownX = 0;
  private lmbDownY = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly clientToWorld?: ClientToWorld
  ) {
    this.canvas.addEventListener('mousemove', this.onMove);
    this.canvas.addEventListener('mousedown', this.onDown);
    window.addEventListener('mouseup', this.onUp);
    this.canvas.addEventListener('contextmenu', this.onContext);
  }

  private onMove = (e: MouseEvent): void => {
    this.clientX = e.clientX;
    this.clientY = e.clientY;
    this.usedThisFrame = true;
  };

  private onDown = (e: MouseEvent): void => {
    if (e.button === 2) {
      this.rmb = true;
      this.usedThisFrame = true;
    } else if (e.button === 0) {
      if (!this.lmb) {
        this.lmbDownAt = e.timeStamp;
        this.lmbDownX = e.clientX;
        this.lmbDownY = e.clientY;
      }
      this.lmb = true;
      this.usedThisFrame = true;
    }
  };

  private onUp = (e: MouseEvent): void => {
    if (e.button === 2) {
      this.rmb = false;
    } else if (e.button === 0) {
      // A quick, still click is a turret shot; a held follow releases silently.
      if (this.lmb && !this.followEngaged(e.timeStamp)) {
        this.firePressed = true; // rising edge; consumed by consumeFire()
        this.fireDir = this.centerOffsetDir(e.clientX, e.clientY);
      }
      this.lmb = false;
    }
  };

  private onContext = (e: MouseEvent): void => {
    e.preventDefault();
  };

  /**
   * Returns the current Focus Aim state derived from the cursor.
   * `held` reflects the RMB state; `dir` is the world-space unit vector from
   * the player to the cursor.
   */
  sample(playerPos: Vec2): FocusAimState {
    let dir: Vec2;
    if (this.clientToWorld) {
      const world = this.clientToWorld(this.clientX, this.clientY);
      dir = normalizeOrZero(world.x - playerPos.x, world.y - playerPos.y);
    } else {
      const rect = this.canvas.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      // Screen y points down; world y points up.
      dir = normalizeOrZero(this.clientX - cx, -(this.clientY - cy));
    }

    return {
      held: this.rmb,
      dir,
    };
  }

  wasUsedThisFrame(): boolean {
    return this.usedThisFrame || this.rmb;
  }

  /** True once the press has become a follow (held ≥250 ms or dragged ≥8 px). */
  private followEngaged(now: number): boolean {
    if (now - this.lmbDownAt >= 250) return true;
    const dx = this.clientX - this.lmbDownX;
    const dy = this.clientY - this.lmbDownY;
    return dx * dx + dy * dy >= 64;
  }

  /**
   * Ship-follow steering while LMB is held: the ship steers toward the
   * cursor, full speed beyond ~120 px from screen center (the ship), easing
   * to a stop as the cursor closes in.
   */
  sampleFollow(now: number): { active: boolean; dir: Vec2; magnitude: number } {
    if (!this.lmb || !this.followEngaged(now)) {
      return { active: false, dir: { x: 0, y: 0 }, magnitude: 0 };
    }
    const rect = this.canvas.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = this.clientX - cx;
    const dy = -(this.clientY - cy); // screen y down → world y up
    const dist = Math.hypot(dx, dy);
    return {
      active: true,
      dir: normalizeOrZero(dx, dy),
      magnitude: Math.min(1, dist / 120),
    };
  }

  /**
   * One-tick rising edge of the left mouse button (manual turret fire), plus
   * the click direction from screen center — the ship sits at the camera
   * anchor, so the center offset is the world direction from the ship.
   * Both are cleared on read, mirroring the keyboard dash/pause edge pattern.
   */
  consumeFire(): { pressed: boolean; dir: Vec2 | null } {
    const out = { pressed: this.firePressed, dir: this.fireDir };
    this.firePressed = false;
    this.fireDir = null;
    return out;
  }

  /** Normalized offset from the canvas center; screen y flips to world y. */
  private centerOffsetDir(clientX: number, clientY: number): Vec2 {
    const rect = this.canvas.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    return normalizeOrZero(clientX - cx, -(clientY - cy));
  }

  clearFrame(): void {
    this.usedThisFrame = false;
  }

  dispose(): void {
    this.canvas.removeEventListener('mousemove', this.onMove);
    this.canvas.removeEventListener('mousedown', this.onDown);
    window.removeEventListener('mouseup', this.onUp);
    this.canvas.removeEventListener('contextmenu', this.onContext);
  }
}
